import { useState, useEffect } from "react";
import { api } from "../api/staff";
import { Shield, Users, Key, ChevronDown, ChevronUp } from "lucide-react";
import { PageHeader } from "../components/common/PageHeader";
import { Badge } from "../components/common/Badge";
import { usePermission } from "../rbac/usePermission";

interface RoleMatrixItem {
  role: string;
  permissions: string[];
}

interface MatrixResponse {
  roles: RoleMatrixItem[];
  allPermissions: string[];
}

const ROLE_DESCRIPTIONS: Record<string, string> = {
  developer: "Accès superadmin complet à l'ensemble du système, aux outils d'infrastructure et aux bypass de sécurité.",
  admin: "Gestion des utilisateurs, des clés de service, des passkeys, des invitations et de la surveillance OSINT.",
  moderator: "Modération des rapports, consultation des utilisateurs et surveillance Discord en lecture seule.",
  support: "Support technique de premier niveau, consultation des abonnements et des invitations.",
};

export function Roles() {
  const { role: currentRole } = usePermission();
  const [data, setData] = useState<MatrixResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    api<MatrixResponse>("/staff/rbac/matrix")
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        Chargement des rôles…
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        title="Rôles & Niveaux d'Accès"
        description="Consultez les privilèges associés à chaque profil staff dans l'infrastructure Collect & Track."
      />

      {/* Grid of Roles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {data.roles.map((r) => {
          const isUserRole = r.role === currentRole;
          const isExpanded = expanded === r.role;
          const isAll = r.permissions.includes("*");

          return (
            <div
              key={r.role}
              className={`bg-card border rounded-2xl overflow-hidden shadow-xs transition-all duration-200 ${
                isUserRole ? "border-primary/60 ring-2 ring-primary/20" : "border-border"
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                      <Shield className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-base capitalize text-foreground flex items-center gap-2">
                        {r.role}
                        {isUserRole && (
                          <Badge variant="default" className="text-[10px] uppercase">
                            Votre Rôle
                          </Badge>
                        )}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {isAll ? "Droits Universels" : `${r.permissions.length} permissions attribuées`}
                      </p>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                  {ROLE_DESCRIPTIONS[r.role] || "Privilèges standards du Backoffice."}
                </p>

                <div className="flex items-center gap-4 text-xs font-medium text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Key className="w-3.5 h-3.5" />
                    {isAll ? "Toutes les permissions" : `${r.permissions.length} actions`}
                  </span>
                </div>
              </div>

              {/* Collapsible permissions toggle */}
              <button
                type="button"
                onClick={() => setExpanded(isExpanded ? null : r.role)}
                className="w-full px-6 py-3 border-t border-border bg-muted/20 hover:bg-muted/40 text-xs font-semibold text-muted-foreground hover:text-foreground flex items-center justify-between transition-colors"
              >
                <span>Détail des permissions ({isAll ? data.allPermissions.length : r.permissions.length})</span>
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {/* Expanded Chips */}
              {isExpanded && (
                <div className="p-6 bg-muted/10 border-t border-border">
                  <div className="flex flex-wrap gap-1.5 max-h-60 overflow-y-auto">
                    {(isAll ? data.allPermissions : r.permissions).map((p) => (
                      <span
                        key={p}
                        className="px-2.5 py-1 rounded-md text-[11px] font-mono font-medium bg-background border border-border text-foreground shadow-2xs"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
